import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = '';
const REGULATIONS = ['GDPR', 'HIPAA', 'SOX', 'PCI-DSS', 'CCPA'];

function ScoreGauge({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#059669' : score >= 50 ? '#d97706' : '#dc2626';
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={10} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.28, fontWeight: 700, color }}>{Math.round(score)}</span>
        <span style={{ fontSize: size * 0.12, color: '#9ca3af' }}>/ 100</span>
      </div>
    </div>
  );
}

function StepProgress({ steps }) {
  if (!steps?.length) return null;
  const completed = steps.filter(s => s.status === 'completed').length;
  const pct = Math.round((completed / steps.length) * 100);
  return (
    <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Audit Progress</span>
        <span style={{ fontWeight: 600, color: '#1565c0' }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#1565c0', borderRadius: 4, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold', color: 'white', background: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', animation: step.status === 'active' ? 'pulse 1.5s infinite' : 'none' }}>
              {step.status === 'completed' ? '✓' : step.status === 'active' ? '●' : '○'}
            </span>
            <span style={{ color: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', fontWeight: step.status === 'active' ? 600 : 400, fontSize: 14 }}>{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ComplianceAnalysis() {
  const { user } = useAuth();
  const [auditStep, setAuditStep] = useState(1);
  const [appDescription, setAppDescription] = useState('');
  const [detectedRegs, setDetectedRegs] = useState([]);
  const [selectedRegs, setSelectedRegs] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [currentCheck, setCurrentCheck] = useState(null);
  const [checks, setChecks] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) { fetchChecks(); fetchFrameworks(); } }, [user]);

  const fetchChecks = async () => {
    try { const r = await fetch(`${API_BASE}/api/compliance`, { credentials: 'include' }); const d = await r.json(); if (d.success) setChecks(d.checks); } catch {}
  };
  const fetchFrameworks = async () => {
    try { const r = await fetch(`${API_BASE}/api/compliance/frameworks`); const d = await r.json(); if (d.success) setFrameworks(d.frameworks); } catch {}
  };

  const pollResult = useCallback((uuid) => {
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/compliance/${uuid}`, { credentials: 'include' });
        const d = await r.json();
        if (d.success) {
          setCurrentCheck(d.check);
          if (d.check.status === 'completed' || d.check.status === 'failed') { clearInterval(poll); setAuditStep(4); fetchChecks(); }
        }
      } catch { clearInterval(poll); }
    }, 2000);
  }, []);

  const handleAutoDetect = async () => {
    setDetecting(true);
    try {
      const r = await fetch(`${API_BASE}/api/compliance/auto-detect`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appDescription }),
      });
      const d = await r.json();
      if (d.success && d.detectedRegulations) {
        setDetectedRegs(d.detectedRegulations);
        setSelectedRegs(d.detectedRegulations.filter(r => r.confidence === 'high').map(r => r.code));
        setAuditStep(2);
      }
    } catch (e) { console.error(e); }
    setDetecting(false);
  };

  const handleRunAudit = async () => {
    if (!selectedRegs.length) return;
    setLoading(true);
    setAuditStep(3);
    try {
      const r = await fetch(`${API_BASE}/api/compliance/check`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentText: appDescription, regulations: selectedRegs }),
      });
      const d = await r.json();
      if (d.success) {
        setCurrentCheck({ uuid: d.uuid, status: 'processing', steps: [] });
        pollResult(d.uuid);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const reset = () => { setAuditStep(1); setCurrentCheck(null); setAppDescription(''); setDetectedRegs([]); setSelectedRegs([]); };

  const toggleReg = (code) => setSelectedRegs(prev => prev.includes(code) ? prev.filter(r => r !== code) : [...prev, code]);

  const r = currentCheck?.results || {};
  const evaluation = r.evaluation || {};
  const gaps = r.gaps?.gaps || [];
  const gapSummary = r.gaps?.summary || {};
  const remediation = r.remediation || {};

  const severityColors = { critical: { bg: '#fef2f2', color: '#dc2626', border: '#dc2626' }, major: { bg: '#fef3c7', color: '#d97706', border: '#d97706' }, minor: { bg: '#ecfdf5', color: '#059669', border: '#059669' } };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', margin: 0 }}>Compliance Audit</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Audit your app or features against regulatory requirements</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {auditStep > 1 && <button onClick={reset} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 14 }}>New Audit</button>}
        </div>
      </div>

      {/* Step 1: Describe App */}
      {auditStep === 1 && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Describe Your App or Feature</h2>
          <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>Tell us what your app does, what data it handles, and who uses it. We'll automatically identify which regulations apply.</p>
          <textarea value={appDescription} onChange={e => setAppDescription(e.target.value)}
            placeholder="Example: Our app is a healthcare scheduling platform that collects patient names, emails, phone numbers, and appointment history. We process credit card payments and serve users in the US and EU..."
            style={{ width: '100%', minHeight: 160, padding: '12px 16px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
          <button onClick={handleAutoDetect} disabled={!appDescription.trim() || detecting}
            style={{ marginTop: 20, padding: '12px 28px', borderRadius: 8, border: 'none', background: '#1565c0', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !appDescription.trim() || detecting ? 0.5 : 1 }}>
            {detecting ? 'Analyzing...' : 'Detect Applicable Regulations'}
          </button>

          {/* Supported Frameworks */}
          {frameworks.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Supported Frameworks</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {frameworks.map(f => (
                  <div key={f.code} style={{ padding: 14, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 600, color: '#1565c0' }}>{f.code}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{f.jurisdiction}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Regulations */}
      {auditStep === 2 && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Detected Regulations</h2>
          <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>Based on your app description, these regulations may apply. Toggle to add or remove before running the audit.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {detectedRegs.map((reg, i) => {
              const isSelected = selectedRegs.includes(reg.code);
              return (
                <div key={i} onClick={() => toggleReg(reg.code)} style={{ padding: 16, borderRadius: 8, border: `2px solid ${isSelected ? '#1565c0' : '#e2e8f0'}`, background: isSelected ? '#eff6ff' : 'white', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isSelected ? '#1565c0' : '#d1d5db'}`, background: isSelected ? '#1565c0' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>{isSelected ? '✓' : ''}</span>
                      <span style={{ fontWeight: 600, fontSize: 16 }}>{reg.code}</span>
                      <span style={{ fontSize: 14, color: '#64748b' }}>{reg.name}</span>
                    </div>
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: reg.confidence === 'high' ? '#dcfce7' : reg.confidence === 'medium' ? '#fef3c7' : '#f1f5f9', color: reg.confidence === 'high' ? '#166534' : reg.confidence === 'medium' ? '#92400e' : '#64748b' }}>{reg.confidence}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#64748b', marginTop: 6, marginLeft: 30 }}>{reg.reason}</p>
                </div>
              );
            })}
          </div>

          {/* Manual add */}
          <div style={{ marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: '#64748b', marginRight: 8 }}>Add more:</span>
            {REGULATIONS.filter(r => !detectedRegs.find(d => d.code === r)).map(r => (
              <button key={r} onClick={() => { toggleReg(r); if (!detectedRegs.find(d => d.code === r)) setDetectedRegs(prev => [...prev, { code: r, name: r, reason: 'Manually added', confidence: 'manual' }]); }}
                style={{ padding: '4px 12px', borderRadius: 16, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', fontSize: 12, marginRight: 6 }}>+ {r}</button>
            ))}
          </div>

          <button onClick={handleRunAudit} disabled={!selectedRegs.length}
            style={{ padding: '12px 28px', borderRadius: 8, border: 'none', background: '#1565c0', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: selectedRegs.length ? 1 : 0.5 }}>
            Run Compliance Audit ({selectedRegs.length} regulation{selectedRegs.length !== 1 ? 's' : ''})
          </button>
        </div>
      )}

      {/* Step 3: Processing */}
      {auditStep === 3 && currentCheck?.status === 'processing' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Running Compliance Audit...</h2>
          <StepProgress steps={currentCheck.steps} />
          <p style={{ color: '#64748b', fontSize: 14 }}>Evaluating your app against {selectedRegs.join(', ')}. This typically takes 30-60 seconds.</p>
        </div>
      )}

      {/* Step 4: Dashboard Results */}
      {auditStep === 4 && currentCheck?.status === 'completed' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Overall Score */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ScoreGauge score={currentCheck.overall_score || evaluation.overallScore || 0} size={130} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Overall Compliance Score</h2>
              <p style={{ color: '#64748b', marginBottom: 12 }}>
                {(currentCheck.overall_score || 0) >= 80 ? 'Good standing — minor gaps to address' :
                 (currentCheck.overall_score || 0) >= 50 ? 'Needs improvement — significant gaps found' :
                 'Critical issues — immediate action required'}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {gapSummary.critical > 0 && <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 600 }}>{gapSummary.critical} Critical</span>}
                {gapSummary.major > 0 && <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fef3c7', color: '#d97706', fontSize: 13, fontWeight: 600 }}>{gapSummary.major} Major</span>}
                {gapSummary.minor > 0 && <span style={{ padding: '4px 12px', borderRadius: 20, background: '#ecfdf5', color: '#059669', fontSize: 13, fontWeight: 600 }}>{gapSummary.minor} Minor</span>}
              </div>
            </div>
          </div>

          {/* Per-Regulation Breakdown */}
          {evaluation.regulationResults?.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Regulation Breakdown</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {evaluation.regulationResults.map((reg, i) => (
                  <div key={i} style={{ padding: 16, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 16 }}>{reg.regulation}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: reg.status === 'compliant' ? '#dcfce7' : reg.status === 'partially_compliant' ? '#fef3c7' : '#fef2f2', color: reg.status === 'compliant' ? '#166534' : reg.status === 'partially_compliant' ? '#92400e' : '#991b1b' }}>
                          {reg.status?.replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontWeight: 700, color: reg.score >= 80 ? '#059669' : reg.score >= 50 ? '#d97706' : '#dc2626' }}>{reg.score}%</span>
                      </div>
                    </div>
                    <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${reg.score}%`, background: reg.score >= 80 ? '#059669' : reg.score >= 50 ? '#d97706' : '#dc2626', borderRadius: 3, transition: 'width 0.5s' }} />
                    </div>
                    {reg.findings?.filter(f => f.status !== 'met').length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {reg.findings.filter(f => f.status !== 'met').slice(0, 5).map((f, j) => (
                          <div key={j} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 6, background: f.status === 'not_met' ? '#fef2f2' : '#fef3c7' }}>
                            <span style={{ fontWeight: 500 }}>{f.requirement}</span>
                            {f.gap && <span style={{ color: '#64748b' }}> — {f.gap}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gaps */}
          {gaps.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Compliance Gaps ({gaps.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {gaps.map((gap, i) => {
                  const sc = severityColors[gap.severity] || severityColors.minor;
                  return (
                    <div key={i} style={{ padding: 14, borderRadius: 8, background: sc.bg, borderLeft: `4px solid ${sc.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{gap.requirement}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{gap.severity}</span>
                          {gap.effort && <span style={{ fontSize: 11, color: '#64748b' }}>Effort: {gap.effort}</span>}
                        </div>
                      </div>
                      <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>{gap.description}</p>
                      {gap.regulation && <span style={{ fontSize: 11, color: '#64748b', display: 'inline-block', marginTop: 4 }}>{gap.regulation}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Remediation Plan */}
          {remediation.remediationPlan?.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Remediation Plan</h3>
              {remediation.quickWins?.length > 0 && (
                <div style={{ marginBottom: 20, padding: 14, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#166534', marginBottom: 8 }}>Quick Wins</h4>
                  {remediation.quickWins.map((w, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                      <span style={{ color: '#059669', flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: 13 }}>{w}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {remediation.remediationPlan.map((item, i) => (
                  <div key={i} style={{ padding: 14, borderRadius: 8, background: '#f8fafc', borderLeft: '4px solid #6366f1', display: 'flex', gap: 12 }}>
                    <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{item.priority || i + 1}</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.action || item.gap}</div>
                      {item.timeline && <span style={{ fontSize: 12, color: '#6366f1', marginRight: 12 }}>Timeline: {item.timeline}</span>}
                      {item.resources && <span style={{ fontSize: 12, color: '#64748b' }}>Resources: {item.resources}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Failed */}
      {currentCheck?.status === 'failed' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20 }}>
          <p style={{ color: '#dc2626', fontWeight: 500 }}>Audit failed: {currentCheck.results?.error || 'Unknown error'}</p>
          <button onClick={reset} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer' }}>Try Again</button>
        </div>
      )}

      {/* History */}
      {checks.length > 0 && auditStep <= 1 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Previous Audits</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {checks.slice(0, 10).map(c => (
              <div key={c.uuid} onClick={() => { setCurrentCheck(c); setAuditStep(4); }} style={{ padding: 14, borderRadius: 8, background: 'white', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{c.title || c.type}</span>
                  <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 12 }}>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {c.overall_score != null && <span style={{ fontWeight: 700, color: c.overall_score >= 80 ? '#059669' : c.overall_score >= 50 ? '#d97706' : '#dc2626' }}>{Math.round(c.overall_score)}%</span>}
                  <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c.status === 'completed' ? '#ecfdf5' : '#fef2f2', color: c.status === 'completed' ? '#059669' : '#dc2626' }}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
        AI-assisted compliance analysis. Validate findings with a compliance officer or legal counsel.
      </p>
    </div>
  );
}
